import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../services/api';
import { toast } from 'react-toastify';
import ReportBlockMenu from '../components/social/ReportBlockMenu';
import VideoMedia from '../components/common/VideoMedia';

// ─── Icons ────────────────────────────────────────────────────────────────────

const HeartIcon = ({ filled, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const CommentIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

// ─── LocationChip ─────────────────────────────────────────────────────────────
// Optional post location (read-side parity with the app). A linked landmark is
// tappable and navigates to its page on the Routes + Landmarks tab
// (/routes?landmark=ID — the same deep-link the share button emits); a plain
// address renders as static text. Renders nothing when neither is present.
const PinIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const LocationChip = ({ post }) => {
  const landmark = post.landmark || null;
  const label = landmark?.name || post.address || null;
  if (!label) return null;
  if (landmark) {
    return (
      <Link to={`/routes?landmark=${landmark.id}`} className="post-location post-location--link">
        <PinIcon size={13} />
        <span>{label}</span>
      </Link>
    );
  }
  return (
    <div className="post-location">
      <PinIcon size={13} />
      <span>{label}</span>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const Avatar = ({ user, size = 38 }) => {
  const style = { width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.16)', display: 'block', flexShrink: 0 };
  if (user?.profileImageUrl) return <img src={user.profileImageUrl} alt="" style={style} />;
  return (
    <div style={{ ...style, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: size * 0.35, color: 'rgba(255,255,255,0.6)' }}>
      {user?.username?.[0]?.toUpperCase()}
    </div>
  );
};

const timeAgo = (date) => {
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Per-slot media type per the frozen data contract. Legacy posts have a null
// mediaTypes field => every slot is treated as 'image'.
const slotType = (post, i) => (post.mediaTypes && post.mediaTypes[i]) || 'image';

// ─── PostModal ────────────────────────────────────────────────────────────────

const PostModal = ({ post: initialPost, me, onClose, onUpdate, onHidePost, onDelete }) => {
  const [post, setPost] = useState(initialPost);
  const [imgIndex, setImgIndex] = useState(0);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const authorId = initialPost.author?.id || initialPost.userId;
  const isOwnPost = me && authorId === me.id;
  const commentInputRef = useRef();
  const commentsEndRef = useRef();
  const images = post.imageUrls || [];
  const author = post.author;

  useEffect(() => {
    api.get(`/feed/posts/${post.id}/comments`)
      .then(({ data }) => setComments(data.comments || []))
      .catch(() => {})
      .finally(() => setCommentsLoading(false));
  }, [post.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { if (fullscreen) setFullscreen(false); else onClose(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, fullscreen]);

  const toggleLike = async () => {
    try {
      if (post.liked) {
        await api.delete(`/feed/posts/${post.id}/like`);
        const u = { ...post, liked: false, likeCount: Math.max(0, post.likeCount - 1) };
        setPost(u); onUpdate?.(u);
      } else {
        await api.post(`/feed/posts/${post.id}/like`);
        const u = { ...post, liked: true, likeCount: (post.likeCount ?? 0) + 1 };
        setPost(u); onUpdate?.(u);
      }
    } catch { toast.error('Action failed'); }
  };

  const deletePost = async () => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await api.delete(`/feed/posts/${post.id}`);
      onDelete?.(post.id);
      onClose();
    } catch { toast.error('Failed to delete post'); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/feed/posts/${post.id}/comments`, { content: commentText.trim() });
      setComments(prev => [...prev, data.comment]);
      const u = { ...post, commentCount: (post.commentCount ?? 0) + 1 };
      setPost(u); onUpdate?.(u);
      setCommentText('');
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch { toast.error('Failed to post comment'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="post-modal-overlay" onClick={onClose}>
      <div className="post-modal" onClick={e => e.stopPropagation()}>
        <div className="post-modal-media">
          {images.length > 0 ? (
            <>
              {slotType(post, imgIndex) === 'video' ? (
                <VideoMedia
                  src={post.videoUrls?.[imgIndex]}
                  poster={images[imgIndex]}
                  className="post-modal-img video-media--contain"
                />
              ) : (
                <img src={images[imgIndex]} alt="" className="post-modal-img" style={{ cursor: 'zoom-in' }} onClick={() => setFullscreen(true)} />
              )}
              {images.length > 1 && imgIndex > 0 && (
                <button className="post-modal-arrow post-modal-arrow-left" onClick={() => setImgIndex(i => i - 1)}><ChevronLeft /></button>
              )}
              {images.length > 1 && imgIndex < images.length - 1 && (
                <button className="post-modal-arrow post-modal-arrow-right" onClick={() => setImgIndex(i => i + 1)}><ChevronRight /></button>
              )}
              {images.length > 1 && (
                <div className="post-modal-dots">
                  {images.map((_, i) => <span key={i} className={i === imgIndex ? 'active' : ''} />)}
                </div>
              )}
            </>
          ) : (
            <div className="post-modal-text-only">{post.content}</div>
          )}
        </div>

        <div className="post-modal-sidebar">
          <div className="post-modal-header">
            <Link to={`/profile/${author?.username}`} className="post-modal-author" onClick={onClose}>
              <Avatar user={author} size={32} />
              <span>{author?.username}</span>
            </Link>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {!isOwnPost && (
                <ReportBlockMenu
                  targetType="post"
                  targetId={post.id}
                  targetUserId={authorId}
                  targetLabel="post"
                  onReported={() => { onHidePost?.(post.id); onClose(); }}
                  onBlocked={() => { onHidePost?.(post.id); onClose(); }}
                  triggerSize="small"
                />
              )}
              {isOwnPost && (
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setMenuOpen(o => !o)}
                    style={{ background: 'none', border: 'none', color: '#8892b0', cursor: 'pointer', padding: '4px 8px', fontSize: '1.1rem', lineHeight: 1, borderRadius: 4 }}
                  >
                    ···
                  </button>
                  {menuOpen && (
                    <>
                      <div style={{ position: 'fixed', inset: 0, zIndex: 1 }} onClick={() => setMenuOpen(false)} />
                      <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 2, background: '#1a2030', border: '1px solid #2a3142', borderRadius: 6, minWidth: 120, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                        <button
                          onClick={() => { setMenuOpen(false); deletePost(); }}
                          style={{ display: 'block', width: '100%', padding: '10px 14px', background: 'none', border: 'none', color: '#e84545', cursor: 'pointer', fontSize: '0.85rem', textAlign: 'left', fontWeight: 500 }}
                        >
                          Delete post
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              <button className="post-modal-close" onClick={onClose}>×</button>
            </div>
          </div>

          <div className="post-modal-comments-area">
            {post.content && (
              <div className="post-modal-caption">
                <Link to={`/profile/${author?.username}`} className="post-modal-caption-user" onClick={onClose}>{author?.username}</Link>
                {post.content}
              </div>
            )}
            <LocationChip post={post} />
            {commentsLoading && <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}><div className="loading-spinner" style={{ width: 22, height: 22, borderWidth: 2 }} /></div>}
            {!commentsLoading && comments.map(c => {
              const cAuthorId = c.author?.id || c.userId;
              const isOwnComment = me && cAuthorId === me.id;
              return (
                <div key={c.id} className="post-modal-comment" style={{ alignItems: 'flex-start' }}>
                  <Link to={`/profile/${c.author?.username}`} onClick={onClose} className="post-modal-comment-avatar">
                    <Avatar user={c.author} size={28} />
                  </Link>
                  <div className="post-modal-comment-body" style={{ flex: 1, minWidth: 0 }}>
                    <Link to={`/profile/${c.author?.username}`} onClick={onClose} className="post-modal-comment-user">{c.author?.username}</Link>
                    <span className="post-modal-comment-text">{c.content}</span>
                  </div>
                  {!isOwnComment && (
                    <ReportBlockMenu
                      targetType="comment"
                      targetId={c.id}
                      targetUserId={cAuthorId}
                      targetLabel="comment"
                      onReported={() => setComments(prev => prev.filter(x => x.id !== c.id))}
                      onBlocked={(blockedUserId) => setComments(prev => prev.filter(x => (x.author?.id || x.userId) !== blockedUserId))}
                      triggerSize="small"
                    />
                  )}
                </div>
              );
            })}
            <div ref={commentsEndRef} />
          </div>

          <div className="post-modal-actions">
            <button className={`post-modal-like-btn${post.liked ? ' liked' : ''}`} onClick={toggleLike}>
              <HeartIcon filled={post.liked} size={22} />
            </button>
            <button className="post-modal-comment-trigger" onClick={() => commentInputRef.current?.focus()}>
              <CommentIcon size={22} />
            </button>
            <span className="post-modal-like-count">{post.likeCount ?? 0} {post.likeCount === 1 ? 'like' : 'likes'}</span>
          </div>

          <form className="post-modal-comment-form" onSubmit={submitComment}>
            <input
              ref={commentInputRef}
              type="text"
              placeholder="Add a comment…"
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              className="post-modal-comment-input"
              autoComplete="off"
            />
            <button type="submit" className="post-modal-comment-submit" disabled={submitting || !commentText.trim()}>Post</button>
          </form>
        </div>
      </div>

      {fullscreen && (
        <div className="post-fullscreen" onClick={() => setFullscreen(false)}>
          {images.length > 1 && imgIndex > 0 && (
            <button className="post-fullscreen-arrow post-fullscreen-arrow-left" onClick={e => { e.stopPropagation(); setImgIndex(i => i - 1); }}><ChevronLeft /></button>
          )}
          {slotType(post, imgIndex) === 'video' ? (
            <VideoMedia
              src={post.videoUrls?.[imgIndex]}
              poster={images[imgIndex]}
              className="post-fullscreen-img video-media--contain"
            />
          ) : (
            <img src={images[imgIndex]} alt="" className="post-fullscreen-img" onClick={e => e.stopPropagation()} />
          )}
          {images.length > 1 && imgIndex < images.length - 1 && (
            <button className="post-fullscreen-arrow post-fullscreen-arrow-right" onClick={e => { e.stopPropagation(); setImgIndex(i => i + 1); }}><ChevronRight /></button>
          )}
          <button className="post-fullscreen-close" onClick={() => setFullscreen(false)}>×</button>
        </div>
      )}
    </div>
  );
};

// ─── FeedPostCard ─────────────────────────────────────────────────────────────

// Clamp to Instagram's bounds (4:5 portrait min, 1.91:1 landscape max) so an
// extreme image can't blow out the card layout. Mirrors app/PostViewer.jsx.
const clampRatio = (w, h) => {
  if (!w || !h) return null;
  return Math.min(Math.max(w / h, 0.8), 1.91);
};

// Memoized so liking/commenting on one card (or appending the next page) doesn't
// re-render every other card. Props (onOpen/onHide) are stabilized with useCallback.
const FeedPostCard = React.memo(({ post: initialPost, onOpen, onHide, currentUserId }) => {
  const [post, setPost] = useState(initialPost);
  const author = post.author;
  const authorId = author?.id || post.userId;
  const isOwnPost = currentUserId && authorId === currentUserId;
  const images = post.imageUrls || [];
  // Feed list uses the lightweight ~1080px WebP thumbnails (full-res is fetched
  // only when the post modal opens); fall back per-slot for legacy posts.
  const thumbs = post.thumbnailUrls || [];
  const [imgIndex, setImgIndex] = useState(0);

  // Prefer dimensions persisted at upload time — that's the no-flicker path.
  // Fall back to onLoad (single-frame flicker) for legacy posts without meta.
  const seededRatio = clampRatio(post.imageMeta?.[0]?.w, post.imageMeta?.[0]?.h);
  const [aspectRatio, setAspectRatio] = useState(seededRatio ?? 1);
  const handleFirstImageLoad = (e) => {
    if (seededRatio) return;
    const { naturalWidth, naturalHeight } = e.currentTarget;
    const ratio = clampRatio(naturalWidth, naturalHeight);
    if (ratio) setAspectRatio(ratio);
  };

  const toggleLike = async (e) => {
    e.stopPropagation();
    try {
      if (post.liked) {
        await api.delete(`/feed/posts/${post.id}/like`);
        setPost(p => ({ ...p, liked: false, likeCount: Math.max(0, p.likeCount - 1) }));
      } else {
        await api.post(`/feed/posts/${post.id}/like`);
        setPost(p => ({ ...p, liked: true, likeCount: (p.likeCount ?? 0) + 1 }));
      }
    } catch { toast.error('Action failed'); }
  };

  return (
    <div className="feed-card">
      <div className="feed-card-header">
        <Link to={`/profile/${author?.username}`} className="feed-card-author">
          <Avatar user={author} size={34} />
          <span className="feed-card-username">{author?.username}</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="feed-card-time">{timeAgo(post.createdAt)}</span>
          {!isOwnPost && authorId && (
            <ReportBlockMenu
              targetType="post"
              targetId={post.id}
              targetUserId={authorId}
              targetLabel="post"
              onReported={() => onHide?.(post.id)}
              onBlocked={() => onHide?.(post.id)}
              triggerSize="small"
            />
          )}
        </div>
      </div>

      <LocationChip post={post} />

      {images.length > 0 && (
        <div className="feed-card-images" style={{ aspectRatio }} onClick={() => onOpen(post)}>
          {slotType(post, imgIndex) === 'video' ? (
            <VideoMedia
              src={post.videoUrls?.[imgIndex]}
              poster={thumbs[imgIndex] || images[imgIndex]}
              className="feed-card-img"
            />
          ) : (
            <img
              src={thumbs[imgIndex] || images[imgIndex]}
              alt=""
              className="feed-card-img"
              loading="lazy"
              decoding="async"
              onLoad={imgIndex === 0 ? handleFirstImageLoad : undefined}
            />
          )}
          {images.length > 1 && imgIndex > 0 && (
            <button className="feed-card-arrow feed-card-arrow-left" onClick={e => { e.stopPropagation(); setImgIndex(i => i - 1); }}><ChevronLeft /></button>
          )}
          {images.length > 1 && imgIndex < images.length - 1 && (
            <button className="feed-card-arrow feed-card-arrow-right" onClick={e => { e.stopPropagation(); setImgIndex(i => i + 1); }}><ChevronRight /></button>
          )}
          {images.length > 1 && (
            <div className="feed-card-dots">
              {images.map((_, i) => <span key={i} className={i === imgIndex ? 'active' : ''} onClick={e => { e.stopPropagation(); setImgIndex(i); }} />)}
            </div>
          )}
        </div>
      )}

      <div className="feed-card-actions">
        <button className={`feed-card-action-btn${post.liked ? ' liked' : ''}`} onClick={toggleLike}>
          <HeartIcon filled={post.liked} size={20} />
        </button>
        <button className="feed-card-action-btn" onClick={() => onOpen(post)}>
          <CommentIcon size={20} />
        </button>
      </div>

      <div className="feed-card-meta">
        <span className="feed-card-likes">{post.likeCount ?? 0} {post.likeCount === 1 ? 'like' : 'likes'}</span>
      </div>

      {post.content?.trim() && (
        <div className="feed-card-caption">
          <Link to={`/profile/${author?.username}`} className="feed-card-caption-user">{author?.username}</Link>
          {' '}{post.content}
        </div>
      )}

      {(post.commentCount ?? 0) > 0 && (
        <button className="feed-card-comments-link" onClick={() => onOpen(post)}>
          View all {post.commentCount} comments
        </button>
      )}
    </div>
  );
});

// ─── Feed page ────────────────────────────────────────────────────────────────

const Feed = () => {
  const { user: me } = useSelector(s => s.auth);
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [openItem, setOpenItem] = useState(null);

  // Hard-list of post IDs already shown — sent to server as excludeIds so
  // "load more" actually returns new posts instead of refetching the same
  // top-scored set and dedup'ing them all out client-side. Mirrors app.
  const loadedIdsRef = useRef([]);
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);
  const hasMoreRef = useRef(true);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  const loadPosts = useCallback(async ({ replace = false } = {}) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (replace) setLoading(true); else setLoadingMore(true);
    try {
      const params = { limit: 20 };
      if (!replace && loadedIdsRef.current.length > 0) {
        params.excludeIds = loadedIdsRef.current.slice(-300).join(',');
      }
      const { data } = await api.get('/feed/ranked', { params });
      // Server now hard-filters to photo posts only, so every returned row is
      // renderable. No client filter → loadedIdsRef captures the complete set
      // the server has shown us, so excludeIds works correctly across pages.
      const incoming = data.posts || [];
      setPosts(prev => {
        const next = replace
          ? incoming
          : (() => {
              const existing = new Set(prev.map(p => p.id));
              return [...prev, ...incoming.filter(p => !existing.has(p.id))];
            })();
        loadedIdsRef.current = next.map(p => p.id);
        return next;
      });
      // Drive hasMore purely from the server's exhausted flag — that's the
      // only source of truth for whether more posts exist in the DB. Don't
      // gate on incoming.length, since a transient zero-result page (e.g.
      // a future ranking quirk) shouldn't permanently kill the scroll.
      setHasMore(!data.exhausted);
    } catch { /* silent */ }
    finally {
      setLoading(false); setLoadingMore(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => { loadPosts({ replace: true }); }, [loadPosts]);

  // Open a specific post when ?post=ID is in the URL (notification deep-link).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const postId = params.get('post');
    if (!postId) return;
    api.get(`/feed/posts/${postId}`)
      .then(({ data }) => {
        const p = data.post || data;
        if (p) setOpenItem(p);
      })
      .catch(() => {});
  }, [location.search]);

  // Infinite scroll via IntersectionObserver — auto-fire load when the
  // sentinel at the bottom of the list enters the viewport.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting && hasMoreRef.current && !loadingRef.current) {
        loadPosts();
      }
    }, { rootMargin: '400px 0px' });
    io.observe(node);
    return () => io.disconnect();
  }, [loadPosts]);

  const handleUpdate = (updated) => {
    setPosts(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
    if (openItem?.id === updated.id) setOpenItem(updated);
  };

  // Remove a post locally after the user reports it or blocks the author —
  // the server now also filters on subsequent fetches, but the optimistic
  // hide keeps the UX immediate.
  const hidePost = useCallback((postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
    loadedIdsRef.current = loadedIdsRef.current.filter(id => id !== postId);
  }, []);

  // Open instantly with the post we already have, then refresh in the background
  // (that GET also bumps the server view count). Guard the late response so it
  // can't re-open a modal the user already closed or switched away from.
  const openPost = useCallback((post) => {
    setOpenItem(post);
    api.get(`/feed/posts/${post.id}`)
      .then(({ data }) => setOpenItem(cur => (cur && cur.id === post.id ? data.post : cur)))
      .catch(() => {});
  }, []);

  return (
    <div className="feed-page">
      <div className="feed-inner">
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <div className="loading-spinner" />
          </div>
        )}

        {!loading && posts.length === 0 && (
          <div className="feed-empty">
            <p>Nothing here yet.</p>
            <p className="feed-empty-sub">Follow other enthusiasts to see their posts here.</p>
          </div>
        )}

        {!loading && posts.map(post => (
          <FeedPostCard
            key={post.id}
            post={post}
            onOpen={openPost}
            onHide={hidePost}
            currentUserId={me?.id}
          />
        ))}

        {/* Sentinel — when this enters the viewport the next page fires.
            Always rendered (even when not hasMore) so the observer ref stays
            stable; we just stop loading once hasMore flips. */}
        <div ref={sentinelRef} style={{ height: 1 }} />

        {loadingMore && hasMore && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <div className="loading-spinner" />
          </div>
        )}

        {!hasMore && !loading && posts.length > 0 && (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem' }}>
            You're all caught up.
          </div>
        )}
      </div>

      {openItem && (
        <PostModal post={openItem} me={me} onClose={() => setOpenItem(null)} onUpdate={handleUpdate} onHidePost={hidePost} onDelete={hidePost} />
      )}
    </div>
  );
};

export default Feed;
